/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { FC } from 'react'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Link, Menu } from 'components/ui'
import { CartButton } from 'modules/cart/components'
import { Bars } from 'components/icons'
import { UserSessionButton } from 'modules/users/components'
import { useCart, useCartModal, useMercadinho } from 'hooks'
import { SearchInput } from 'modules/shared/containers'
import Image from 'next/image'
import { ROUTE_PRODUCT_INDEX } from 'routes/products'
import { CategoryService } from 'modules/categories/services'
import { Categories } from 'framework/categories'
import Portal from '@reach/portal'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { BrowserView, MobileView } from 'react-device-detect'
import { Menu as ReactMenu, MenuItem, SubMenu, MenuButton } from '@szhsin/react-menu'
import '@szhsin/react-menu/dist/index.css'
import { parseCookies } from 'nookies'
import { isPedidoFacilSession } from 'modules/pedido-facil/utils/pedido-facil-session'
import type { NavbarProps } from '.'
import FilterLink from './filter-link'
import s from './navbar.module.css'
import DeliveryArea from '../delivery-area'

const Navbar: FC<NavbarProps> = () => {
  const { toggleCartModal } = useCartModal()
  const cookies = parseCookies()
  const storedZipCode = (cookies.zipcode as never) || ''
  const { isMercadinho } = useMercadinho()

  const router = useRouter()
  const isPedidoFacilPage = router.pathname === '/pedido-facil'
  const inPedidoFacilSession = isPedidoFacilSession(router.pathname)
  const { items } = useCart()

  const [categories, setCategories] = useState<Categories>(null as unknown as Categories)

  useEffect(() => {
    async function getCategories() {
      const categoryService = new CategoryService()
      const response = await categoryService.getCategories()
      setCategories(categoryService.parser(response.data.data))
    }
    getCategories().catch(() => {})
  }, [])

  const [menu, setMenu] = useState(false)

  function onButtonCartClick() {
    if (router.asPath.includes('checkout')) {
      const basePath = '/carrinho'
      const searchParams = new URLSearchParams(window.location.search)

      if (isMercadinho) {
        searchParams.append('ismercadinho', 'true')
      }
      const fullPath = `${basePath}?${searchParams.toString()}`
      router.push(fullPath)
      return
    }
    toggleCartModal()
  }

  useEffect(() => {
    if (menu) {
      disableBodyScroll(document.body)
    }

    return () => clearAllBodyScrollLocks()
  }, [menu])

  return (
    <>
      <div className={s.root}>
        <header className={s.header}>
          <div className={s.logo}>
            {isMercadinho ? (
              <Image
                src="/assets/images/logo_facil.png"
                alt={process.env.NEXT_PUBLIC_SITE_NAME}
                width="100px"
                height="55px"
              />
            ) : (
              <Link href={inPedidoFacilSession ? '/pedido-facil' : '/'}>
                <Image
                  src="/assets/images/logo.png"
                  alt={process.env.NEXT_PUBLIC_SITE_NAME}
                  width="44px"
                  height="44px"
                />
              </Link>
            )}
          </div>

          {!isMercadinho && (
            <BrowserView>
              <DeliveryArea />
            </BrowserView>
          )}

          <BrowserView viewClassName={s.desktop}>
            {categories && !isMercadinho && !isPedidoFacilPage && (
              <ReactMenu
                menuButton={
                  <MenuButton className={s.menuCategories}>
                    <Bars />
                    <span>Categorias</span>
                  </MenuButton>
                }
              >
                {categories.map((item) =>
                  item?.categories && item?.categories?.length > 0 ? (
                    <SubMenu
                      label={<span onClick={() => router.push(ROUTE_PRODUCT_INDEX + item.slug)}>{item.name}</span>}
                    >
                      {(item?.categories || []).map((cat) => (
                        <MenuItem href={ROUTE_PRODUCT_INDEX + cat.slug}>{cat.name}</MenuItem>
                      ))}
                    </SubMenu>
                  ) : (
                    <MenuItem href={ROUTE_PRODUCT_INDEX + item.slug}>{item.name}</MenuItem>
                  ),
                )}
              </ReactMenu>
            )}
          </BrowserView>
          <div className={s.search}>
            <SearchInput />
          </div>
          {!isMercadinho && !isPedidoFacilPage && (
            <BrowserView viewClassName={s.desktop}>
              <Menu
                label="Entregas"
                items={[
                  {
                    href: `${ROUTE_PRODUCT_INDEX}?days=Segunda-Feira&zipcode=${storedZipCode}`,
                    label: 'Segunda-feira',
                  },
                  { href: `${ROUTE_PRODUCT_INDEX}?days=Terca-Feira&zipcode=${storedZipCode}`, label: 'Terça-feira' },
                  { href: `${ROUTE_PRODUCT_INDEX}?days=Quarta-Feira&zipcode=${storedZipCode}`, label: 'Quarta-feira' },
                  { href: `${ROUTE_PRODUCT_INDEX}?days=Quinta-Feira&zipcode=${storedZipCode}`, label: 'Quinta-feira' },
                  { href: `${ROUTE_PRODUCT_INDEX}?days=Sexta-Feira&zipcode=${storedZipCode}`, label: 'Sexta-feira' },
                  { href: `${ROUTE_PRODUCT_INDEX}?days=Sabado&zipcode=${storedZipCode}`, label: 'Sábado' },
                ]}
              />
              <Link className={s.link} href={`/produtos/experiencias?zipcode=${storedZipCode}`}>
                Experiências
              </Link>
              <Link className={s.link} href={`/produtos?zipcode=${storedZipCode}`}>
                Produtos
              </Link>
              <Link className={s.link} href="/seja-um-vendedor">
                Quero vender
              </Link>
            </BrowserView>
          )}
          <CartButton total={items.length || 0} onClick={onButtonCartClick} />
          {!isMercadinho && !isPedidoFacilPage && (
            <button type="button" className={s.menu} aria-label="toggle menu" onClick={() => setMenu(!menu)}>
              <Bars />
            </button>
          )}
          <BrowserView viewClassName={s.session}>
            <UserSessionButton />
          </BrowserView>
        </header>
        <MobileView>
          <Portal>
            {menu && !isPedidoFacilPage ? (
              <div className={s.panel}>
                <div className={s.columns}>
                  <Link className={s.link} href={`/produtos?zipcode=${storedZipCode}`}>
                    Produtos
                  </Link>
                  <Link className={s.link} href="/produtos/experiencias">
                    Experiências
                  </Link>
                  <Menu
                    label="Entregas"
                    onClickCallback={() => setMenu(!menu)}
                    items={[
                      {
                        href: `${ROUTE_PRODUCT_INDEX}?days=Segunda-Feira&zipcode=${storedZipCode}`,
                        label: 'Segunda-feira',
                      },
                      {
                        href: `${ROUTE_PRODUCT_INDEX}?days=Terca-Feira&zipcode=${storedZipCode}`,
                        label: 'Terça-feira',
                      },
                      {
                        href: `${ROUTE_PRODUCT_INDEX}?days=Quarta-Feira&zipcode=${storedZipCode}`,
                        label: 'Quarta-feira',
                      },
                      {
                        href: `${ROUTE_PRODUCT_INDEX}?days=Quinta-Feira&zipcode=${storedZipCode}`,
                        label: 'Quinta-feira',
                      },
                      {
                        href: `${ROUTE_PRODUCT_INDEX}?days=Sexta-Feira&zipcode=${storedZipCode}`,
                        label: 'Sexta-feira',
                      },
                      { href: `${ROUTE_PRODUCT_INDEX}?days=Sabado&zipcode=${storedZipCode}`, label: 'Sábado' },
                    ]}
                  />
                  <UserSessionButton />
                </div>
                <div className={s.categories}>
                  {categories && (
                    <FilterLink
                      callback={() => setMenu(!menu)}
                      filters={categories}
                      preserveQuery={false}
                      baseUrl={ROUTE_PRODUCT_INDEX}
                    />
                  )}
                </div>
                <Link className={s.link} href="/seja-um-vendedor">
                  Quero vender
                </Link>
              </div>
            ) : null}
          </Portal>
        </MobileView>
      </div>
    </>
  )
}

export default Navbar
